import { useState } from 'react';
import { Users, Activity, MousePointerClick, CalendarRange } from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { useInternalAdoption } from '@/hooks/useInternalPanel';
import { InternalTable, EmptyState, LoaderBlock, RatioBar, StatCard, fmtDay, fmtNum, fmtDateTime } from './shared';
import { useTranslation } from 'react-i18next';

export function AdoptionTab() {
  const { t } = useTranslation();
  const [days, setDays] = useState<7 | 30>(7);
  const { data, isLoading } = useInternalAdoption(days);

  if (isLoading) return <LoaderBlock />;
  if (!data) return <EmptyState title={t('internal.adoptiontab.sin_datos_de_adopcion')} />;

  const hasData = data.perDay.some((d) => d.calls > 0 || d.pageViews > 0);
  const maxModuleCalls = data.modules[0]?.calls ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('internal.adoptiontab.uso_real_de_la_plataforma_por')}
        </p>
        <div className="flex gap-1">
          {([7, 30] as const).map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 h-8 rounded-md text-sm border ${
                days === d ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'
              }`}
            >
              {d} {t('internal.adoptiontab.dias')}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Users} label={t('internal.adoptiontab.usuarios_unicos')} value={fmtNum(data.uniqueUsers)} sub={t('internal.adoptiontab.ultimos_n_dias', { n: data.rangeDays })} tone="blue" />
        <StatCard icon={CalendarRange} label="WAU" value={fmtNum(data.wau)} sub={t('internal.adoptiontab.activos_ultimos_7_dias')} tone="purple" />
        <StatCard icon={Activity} label={t('internal.adoptiontab.llamadas_api')} value={fmtNum(data.perDay.reduce((a, d) => a + d.calls, 0))} tone="green" />
        <StatCard icon={MousePointerClick} label={t('internal.adoptiontab.page_views')} value={fmtNum(data.perDay.reduce((a, d) => a + d.pageViews, 0))} tone="amber" />
      </div>

      {!hasData ? (
        <EmptyState
          title={t('internal.adoptiontab.todavia_no_hay_datos_acumulados')}
          description={t('internal.adoptiontab.la_telemetria_arranca_con_este_deploy')}
        />
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5">
              <h3 className="text-sm font-semibold mb-3">{t('internal.adoptiontab.usuarios_activos_por_dia')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.perDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tickFormatter={fmtDay} fontSize={11} />
                  <YAxis allowDecimals={false} fontSize={11} width={32} />
                  <Tooltip labelFormatter={(v) => fmtDay(String(v))} />
                  <Bar dataKey="users" name="Usuarios" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5">
              <h3 className="text-sm font-semibold mb-3">{t('internal.adoptiontab.actividad_por_dia')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.perDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tickFormatter={fmtDay} fontSize={11} />
                  <YAxis allowDecimals={false} fontSize={11} width={40} />
                  <Tooltip labelFormatter={(v) => fmtDay(String(v))} />
                  <Line type="monotone" dataKey="calls" name="Llamadas API" stroke="#3b82f6" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="pageViews" name="Page views" stroke="#f59e0b" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5">
              <h3 className="text-sm font-semibold mb-3">{t('internal.adoptiontab.modulos_mas_usados')}</h3>
              {data.modules.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">{t('internal.adoptiontab.sin_llamadas_registradas')}</p>
              ) : (
                <div className="space-y-2.5">
                  {data.modules.slice(0, 15).map((m) => (
                    <div key={m.module}>
                      <div className="flex items-baseline justify-between text-sm mb-1">
                        <span className="font-medium">{m.module}</span>
                        <span className="text-xs text-muted-foreground">
                          {t('internal.adoptiontab.n_llamadas_m_usuarios', { n: fmtNum(m.calls), m: fmtNum(m.uniqueUsers) })}
                        </span>
                      </div>
                      <RatioBar value={m.calls} max={maxModuleCalls} />
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-muted-foreground mt-4">
                {t('internal.adoptiontab.el_chat_del_brain_corre_por')}
              </p>
            </Card>
            <Card className="p-5">
              <h3 className="text-sm font-semibold mb-3">{t('internal.adoptiontab.top_usuarios')}</h3>
              <InternalTable
                rows={data.topUsers}
                rowKey={(u) => u.userEmail}
                columns={[
                  { key: 'user', label: t('internal.adoptiontab.usuario'), className: 'truncate max-w-[220px]', render: (u) => u.userEmail },
                  { key: 'calls', label: t('internal.adoptiontab.eventos'), align: 'right', render: (u) => fmtNum(u.calls) },
                  { key: 'modules', label: t('internal.adoptiontab.modulos'), align: 'right', render: (u) => fmtNum(u.modules) },
                  { key: 'last', label: t('internal.adoptiontab.ultima_vez'), align: 'right', className: 'text-xs text-muted-foreground', render: (u) => fmtDateTime(u.lastSeen) },
                ]}
              />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
